/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Checkbox, ChoiceGroup, IChoiceGroupOption, TextField } from "@fluentui/react";

import { IFieldDefinitionPanelToggleProps } from ".";
import { FieldDefinitionPanelToggleCommands } from "./FieldDefinitionPanelToggleViewModel";
import { IFieldDefinitionPanelToggleState } from "./FieldDefinitionPanelToggleInterfaces";
import { panelFooterProps } from "./FieldDefinitionPanelStackTokens";
import { IPanelFooterProps } from "./IPanelFooterProps";
import FieldDefinitionPanel from "./FieldDefinitionPanel";

const fieldDefinitionPanelToggleCommands = new FieldDefinitionPanelToggleCommands();
const cmds = fieldDefinitionPanelToggleCommands.Cmds;

const options: IChoiceGroupOption[] = [
    { key: "false", text: "Off" },
    { key: "true", text: "On" }
];

const FieldDefinitionPanelToggle = (props: IFieldDefinitionPanelToggleProps): JSX.Element => {
    const initialState: IFieldDefinitionPanelToggleState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelToggleCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        if (props.common.onSaveContent !== undefined) {
            const data = fieldDefinitionPanelToggleCommands.Save(state);
            props.common.onSaveContent(data);
        }
    };

    const onDefaultValueChange = (
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        selection?: IChoiceGroupOption
    ) => {
        if (selection !== undefined) {
            const on = selection.key === "true";
            dispatch(cmds.onDefaultValueChanged(on));
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: props.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <FieldDefinitionPanel
            title={props.common.panelTitle}
            isOpen={props.common.isOpen}
            onClosePanel={props.common.onClosePanel}
            footerProps={footerProps}
            settingName={state.data.settingName}
            onChangeSettingName={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            settingNameErrorMessage={state.touched.settingName ? state.errors.settingName : ""}
            onBlurSettingName={name => dispatch(cmds.onBlur(name))}
            settingNameReadOnly={!props.common.isNew}
            titleValue={state.data.title}
            onChangeTitle={(name, value) => dispatch(cmds.onTextChanged({ name, value }))}
            titleErrorMessage={state.touched.title ? state.errors.title : ""}
            onBlurTitle={name => dispatch(cmds.onBlur(name))}
            tooltipText={state.data.tooltip}
            helperText={state.data.helper}
            onHelpTextChange={changeData => {
                dispatch(cmds.onHelpTextChange(changeData));
            }}
            accessLevel={state.data.accessLevels}
            onAccessLevelChange={accessLevel => dispatch(cmds.onAccessLevelChanged(accessLevel))}
            auditChanges={state.data.auditChanges}
            onAuditChangesChanged={(checked: boolean) => dispatch(cmds.onAuditChanged(checked))}
            controlType={state.data.definitionType}
        >
            <ChoiceGroup
                label="Default value"
                selectedKey={state.data.defaultValue.toString()}
                options={options}
                onChange={onDefaultValueChange}
            />

            <Checkbox
                label="Include toggle labels"
                defaultChecked={props.data.includeToggleLabels}
                onChange={() => dispatch(cmds.onIncludeToggleLabelsChanged())}
            />
            {state.data.includeToggleLabels && (
                <>
                    <TextField
                        label={`"Off" label`}
                        name="offLabel"
                        required
                        value={state.data.offLabel}
                        errorMessage={state.errors.offLabel}
                        onChange={(e, value) =>
                            dispatch(
                                cmds.onTextChanged({
                                    name: e.currentTarget.name,
                                    value
                                })
                            )
                        }
                        onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    />
                    <TextField
                        label={`"On" label`}
                        name="onLabel"
                        required
                        value={state.data.onLabel}
                        errorMessage={state.errors.onLabel}
                        onChange={(e, value) =>
                            dispatch(
                                cmds.onTextChanged({
                                    name: e.currentTarget.name,
                                    value
                                })
                            )
                        }
                        onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    />
                </>
            )}
        </FieldDefinitionPanel>
    );
};

const TogglePanel = FieldDefinitionPanelToggle;
export { TogglePanel };
